import {createRef, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import type {User} from 'nutshell-core/users/users-types';

import type {Tab} from 'shells/control-bar/page-control-bar';
import {getWidgetIconForType} from 'shells/widgets/helpers';

import type {AccountPageFragment as Account} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../../../store';
import {MINI_HEADER_HEIGHT_WITHOUT_FORM} from '../../../../core-entity/mini-header/mini-header';
import {
    getWidgetComponents,
    getWidgetFallbackLabel,
} from '../../../../core-entity/util/widget-components';
import {TimelineWidget} from '../../../../core-entity/widgets/timeline-widget';
import {getWidgetTabCount} from '../../../../core-entity/workspace/helpers';
import {WorkspaceSection as EntityWorkspaceSection} from '../../../../core-entity/workspace/workspace-section';
import {useGetAccountEnrichment} from '../../../../nutshell-intelligence-enrichment/company-enrichment/graphql/hooks';
import {useGetMcfxViqContacts} from '../../../../people-iq/graphql/hooks/use-get-mcfx-viq-contacts';
import {dismissForAccount} from '../../../../people-iq/redux/actions';
import * as peopleIqSelectors from '../../../../people-iq/redux/selectors';

import {AccountCompanyIqBanner} from '../company-iq/account-company-iq-banner';
import {AccountPeopleIqBanner} from '../people-iq/account-people-iq-banner';

import {useGetWidgetCounts} from './helpers';

type Props = {
    account: Account;
    scrollContainerRef: HTMLDivElement | null | undefined;
    isSidebarCollapsed: boolean;
    isWorkspaceSidebarCollapsed: boolean;
    onIsWorkspaceSidebarCollapsedChange: (isCollapsed: boolean) => void;
    user: User;
    hasFullHeightSidebar?: boolean;
};

export function WorkspaceSection(props: Props) {
    const {
        account,
        scrollContainerRef,
        isSidebarCollapsed,
        isWorkspaceSidebarCollapsed,
        onIsWorkspaceSidebarCollapsedChange,
        hasFullHeightSidebar,
    } = props;

    const workspaceSectionRef = useRef<HTMLDivElement>(null);
    const [isCompanyEnrichmentDismissed, setIsCompanyEnrichmentDismissed] =
        useState<boolean>(false);
    const dispatch = useDispatch();
    const hasDismissedPeopleIq = useSelector((state: NutshellState) =>
        peopleIqSelectors.getHasDismissedForAccount(state, account.id)
    );

    const widgetCounts = useGetWidgetCounts(account.id);
    const {accountEnrichment} = useGetAccountEnrichment(account.id);
    const {mcfxViqContacts: contacts} = useGetMcfxViqContacts(account.id);

    const shouldShowCompanyEnrichment = Boolean(accountEnrichment && accountEnrichment.length > 0);

    const workspaceWidgets = useMemo(() => {
        return account.workspaceWidgets ?? [];
    }, [account.workspaceWidgets]);

    const visibleWidgetObjects = useMemo(() => {
        return workspaceWidgets.filter((widget) => !widget.isHidden);
    }, [workspaceWidgets]);

    const dynamicWorkspaceWidgetComponents = getWidgetComponents({
        mode: 'account',
        entityId: account.id,
        widgets: visibleWidgetObjects,
        user: props.user,
    });

    const enrichmentWidget = useMemo(() => {
        if (!shouldShowCompanyEnrichment || !accountEnrichment || isCompanyEnrichmentDismissed) {
            return null;
        }

        return (
            <AccountCompanyIqBanner
                key='account-company-enrichment'
                accountId={account.id}
                companyName={account.name}
                accountEnrichment={accountEnrichment}
                onDismiss={() => setIsCompanyEnrichmentDismissed(true)}
            />
        );
    }, [
        account.id,
        account.name,
        accountEnrichment,
        isCompanyEnrichmentDismissed,
        shouldShowCompanyEnrichment,
    ]);

    const peopleIqWidget = useMemo(() => {
        if (!contacts || contacts.length === 0 || hasDismissedPeopleIq) {
            return null;
        }

        return (
            <AccountPeopleIqBanner
                key='account-people-iq'
                accountId={account.id}
                accountName={account.name}
                contacts={contacts}
                onDismiss={() => dispatch(dismissForAccount({accountId: account.id}))}
            />
        );
    }, [account.id, account.name, contacts, dispatch, hasDismissedPeopleIq]);

    const displayedWidgetComponents = useMemo(() => {
        return [
            ...(enrichmentWidget ? [enrichmentWidget] : []),
            ...(peopleIqWidget ? [peopleIqWidget] : []),
            ...dynamicWorkspaceWidgetComponents,
            <TimelineWidget
                key='timeline'
                entityId={account.id}
                entityName='Accounts'
                scrollContainerRef={scrollContainerRef}
                hideCreator={true}
                useContainerScroll={true}
            />,
        ];
    }, [
        account.id,
        enrichmentWidget,
        peopleIqWidget,
        dynamicWorkspaceWidgetComponents,
        scrollContainerRef,
    ]);

    const tabOptions: Array<Tab> = useMemo(() => {
        return [
            ...visibleWidgetObjects.map((widget) => ({
                id: widget.id,
                label: getWidgetFallbackLabel(widget.type, widget.name),
                icon: getWidgetIconForType(widget.type),
                count: getWidgetTabCount(widget.type, widgetCounts),
                hideTooltip: !isSidebarCollapsed,
            })),
            {
                id: 'timeline',
                label: 'Timeline',
                icon: getWidgetIconForType('TIMELINE'),
                hideTooltip: !isSidebarCollapsed,
            },
        ];
    }, [isSidebarCollapsed, visibleWidgetObjects, widgetCounts]);

    const tabRefsMap = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});
    const tabRefs = useMemo(() => {
        return tabOptions.reduce<Record<string, React.RefObject<HTMLDivElement>>>((map, tab) => {
            const existing = tabRefsMap.current[tab.id];
            if (existing) {
                map[tab.id] = existing;
            } else {
                const refObj = createRef<HTMLDivElement>();
                tabRefsMap.current[tab.id] = refObj;
                map[tab.id] = refObj;
            }

            return map;
        }, {});
    }, [tabOptions]);

    return (
        <EntityWorkspaceSection
            tabs={tabOptions}
            widgets={displayedWidgetComponents}
            isCollapsed={isWorkspaceSidebarCollapsed}
            onIsCollapsedChange={onIsWorkspaceSidebarCollapsedChange}
            workspaceSectionRef={workspaceSectionRef}
            scrollContainerRef={scrollContainerRef}
            tabRefs={tabRefs}
            stickyHeaderOffset={MINI_HEADER_HEIGHT_WITHOUT_FORM}
            hasFullHeightSidebar={hasFullHeightSidebar}
        />
    );
}
